import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormGroup,
  FormControlLabel,
  InputLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TUserRole } from '../../../../../types';
import ExtraWidthButton from '../../../../../components/Buttons/ExtraWidthButton';
import CloseButton from '../../../../../components/Buttons/CloseButton';
import { isValidMail } from '../../../../../helpers';
import { debounce } from 'lodash';
import { TCreateUser } from '../../../../../store/slices/user';

const InviteFormGroup = styled(FormGroup)({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: '24px',
});

const Asterisk = styled('span')(({ theme }) => ({
  color: theme.palette.error.main,
}));

const CloseButtonContainer = styled('div')({
  position: 'absolute',
  right: '8px',
});

interface IInviteUserProps {
  open: boolean;
  onClose: () => void;
  onInvite: (newUser: TCreateUser) => void;
  roles: TUserRole[] | null | undefined;
}

const InviteUser: React.FC<IInviteUserProps> = ({ open, onClose, onInvite, roles }) => {
  const { t } = useTranslation();

  const [firstName, setFirstName] = useState<string | null>();
  const [lastName, setLastName] = useState<string | null>();
  const [email, setEmail] = useState<string | null>();
  const [phone, setPhone] = useState<string | null>();
  const [userRoles, setUserRoles] = useState<string[]>([]);

  const [formIsValid, setFormIsValid] = useState<boolean>(false);
  const [mailIsValid, setMailIsValid] = useState<boolean>(false);
  const [showMailError, setShowMailError] = useState<boolean>(false);

  const cleanForm = () => {
    setFirstName(null);
    setLastName(null);
    setEmail(null);
    setPhone(null);
    setShowMailError(false);
    setUserRoles([]);
  };

  const handleSubmit = () => {
    if (firstName && lastName && email && userRoles.length) {
      //send invite
      onInvite({
        firstName,
        lastName,
        displayName: `${firstName} ${lastName}`,
        emailAddress: email,
        phone: phone || undefined,
        roleIds: userRoles,
      });

      // clear and close form
      cleanForm();
      onClose();
    }
  };

  const handleClose = () => {
    cleanForm();
    onClose();
  };

  const handleMailError = useMemo(
    () =>
      debounce((isValid) => {
        if (isValid) {
          setShowMailError(false);
        } else {
          setShowMailError(true);
        }
      }, 500),
    [],
  );

  const handleRoleSelect = (roleId: string) => {
    if (userRoles?.includes(roleId)) {
      setUserRoles(userRoles.filter((role) => role !== roleId));
    } else {
      setUserRoles((prevRoles) => [...prevRoles, roleId]);
    }
  };

  useEffect(() => {
    if (email) {
      const isValid = isValidMail(email);
      setMailIsValid(!!isValid);
      handleMailError(isValid);
    }
  }, [email, handleMailError]);

  useEffect(() => {
    if (firstName && lastName && email && userRoles.length && mailIsValid) {
      setFormIsValid(true);
    } else {
      setFormIsValid(false);
    }
  }, [firstName, lastName, email, userRoles, mailIsValid]);

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { minWidth: '414px' } }}>
      <CloseButtonContainer>
        <CloseButton onClick={handleClose} />
      </CloseButtonContainer>
      <DialogTitle>{t('administration:userManagement:invite:title')}</DialogTitle>
      <DialogContent>
        <InviteFormGroup>
          <InputLabel shrink htmlFor='firstName'>
            {t('administration:userManagement:invite:firstName')}
            <Asterisk>*</Asterisk>
          </InputLabel>
          <TextField
            autoFocus
            id='firstName'
            type='text'
            size='small'
            color='secondary'
            fullWidth
            placeholder={t('administration:userManagement:invite:placeholder:firstName')}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
        </InviteFormGroup>
        <InviteFormGroup>
          <InputLabel shrink htmlFor='lastName'>
            {t('administration:userManagement:invite:lastName')}
            <Asterisk>*</Asterisk>
          </InputLabel>
          <TextField
            autoFocus
            id='lastName'
            type='text'
            size='small'
            color='secondary'
            fullWidth
            placeholder={t('administration:userManagement:invite:placeholder:lastName')}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </InviteFormGroup>
        <InviteFormGroup>
          <InputLabel shrink htmlFor='email'>
            {t('administration:userManagement:invite:email:label')}
            <Asterisk>*</Asterisk>
          </InputLabel>
          <TextField
            id='email'
            type='email'
            size='small'
            color='secondary'
            fullWidth
            placeholder={t('administration:userManagement:invite:placeholder:email')}
            value={email}
            error={showMailError}
            helperText={showMailError ? t('administration:userManagement:invite:email:invalid') : ''}
            onChange={(event) => setEmail(event.target.value)}
          />
        </InviteFormGroup>
        <InviteFormGroup>
          <InputLabel shrink htmlFor='phone'>
            {t('administration:userManagement:invite:phone')}
          </InputLabel>
          <TextField
            id='phone'
            type='phone'
            size='small'
            color='secondary'
            fullWidth
            placeholder={t('administration:userManagement:invite:placeholder:phone')}
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </InviteFormGroup>
        <InputLabel shrink htmlFor='roles'>
          {t('administration:userManagement:invite:role')}
          <Asterisk>*</Asterisk>
        </InputLabel>
        <FormGroup id='roles'>
          {roles?.map((role) => (
            <Tooltip key={role.roleId} title={t(`administration:roles:tooltips:${role.i18nKey}`)} placement='top' arrow>
              <FormControlLabel
                control={
                  <Checkbox
                    color='secondary'
                    checked={userRoles?.includes(role.roleId)}
                    onChange={() => handleRoleSelect(role.roleId)}
                  />
                }
                label={t(`administration:roles:${role.i18nKey}`)}
              />
            </Tooltip>
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-around', marginBottom: '16px' }}>
        <ExtraWidthButton onClick={handleClose} color='info' variant='outlined'>
          {t('common:cancel')}
        </ExtraWidthButton>
        <ExtraWidthButton onClick={handleSubmit} variant='contained' color='info' disabled={!formIsValid}>
          {t('administration:userManagement:invite:submit')}
        </ExtraWidthButton>
      </DialogActions>
    </Dialog>
  );
};

export default InviteUser;
